import {
  BLACK_AND_WHITE_TILE_NUMBERS,
  type BlackAndWhiteViewerState,
  getTileColor,
  type TileColor,
  type TileNumber,
  type ViewerCompletedRound,
  type ViewerPlayer,
  type ViewerRoundInProgress,
  type ViewerSubmission,
} from "@cloudnest/black-and-white-engine";
import { useEffect, useRef, useState } from "react";
import { navigateTo } from "../../router";
import { useGameEventsWS } from "../../shared/hooks/use-game-events-ws";
import { useAuth } from "../../shared/store/auth-store";
import { useLobby, useLobbyActions } from "../../shared/store/lobby-store";
import { getParticipantDisplayName } from "../../shared/utils/participants";
import {
  blackAndWhiteMultiplayerActions,
  useBlackAndWhiteMultiplayerStore,
} from "./multiplayer-game-store";
import "./styles/black-and-white-tokens.css";

type RoundCenter = "future" | "pending" | "win" | "loss" | "draw";
type FirstPlayerPolicy = "random" | "host" | "guest";

interface TileView {
  readonly color: TileColor;
  readonly tile: TileNumber | null;
  readonly provisional?: boolean;
}

interface RoundSlot {
  readonly roundNumber: number;
  readonly mine: TileView | null;
  readonly opponent: TileView | null;
  readonly center: RoundCenter;
  readonly current: boolean;
  readonly pendingSide: "mine" | "opponent" | null;
}

function toTileView(submission: ViewerSubmission | null): TileView | null {
  if (submission === null) return null;
  return {
    color: submission.color,
    tile: submission.tile,
  };
}

function getPlayer(state: BlackAndWhiteViewerState, playerId: string | null) {
  return state.players.find((player) => player.playerId === playerId) ?? null;
}

function getOpponentId(
  state: BlackAndWhiteViewerState,
  myPlayerId: string | null,
): string | null {
  return (
    state.players.find((player) => player.playerId !== myPlayerId)?.playerId ??
    null
  );
}

function getActivePlayerId(round: ViewerRoundInProgress | null): string | null {
  if (!round) return null;
  return round.phase === "FIRST_SELECTION"
    ? round.firstPlayerId
    : round.secondPlayerId;
}

function getCurrentSubmissionForPlayer(
  round: ViewerRoundInProgress,
  playerId: string | null,
): ViewerSubmission | null {
  if (!playerId) return null;
  return round.firstSubmission?.playerId === playerId
    ? round.firstSubmission
    : null;
}

function getCompletedSubmissionForPlayer(
  round: ViewerCompletedRound,
  playerId: string | null,
): ViewerSubmission | null {
  if (!playerId) return null;
  if (round.firstSubmission.playerId === playerId) return round.firstSubmission;
  if (round.secondSubmission.playerId === playerId) {
    return round.secondSubmission;
  }
  return null;
}

function getCompletedCenter(
  round: ViewerCompletedRound,
  leftPlayerId: string | null,
): RoundCenter {
  if (round.winnerId === null) return "draw";
  return round.winnerId === leftPlayerId ? "win" : "loss";
}

function buildRoundSlots(
  state: BlackAndWhiteViewerState,
  leftPlayerId: string | null,
  rightPlayerId: string | null,
  previewPlayerId: string | null,
  selectedTile: TileNumber | null,
  revealExactTiles: boolean,
): readonly RoundSlot[] {
  const activePlayerId = getActivePlayerId(state.currentRound);
  const canPreview =
    selectedTile !== null &&
    state.status === "IN_PROGRESS" &&
    activePlayerId === previewPlayerId;

  return BLACK_AND_WHITE_TILE_NUMBERS.map((roundNumber) => {
    const completedRound = state.completedRounds.find(
      (round) => round.roundNumber === roundNumber,
    );
    if (completedRound) {
      return {
        roundNumber,
        mine: maskTileView(
          toTileView(
            getCompletedSubmissionForPlayer(completedRound, leftPlayerId),
          ),
          revealExactTiles,
        ),
        opponent: maskTileView(
          toTileView(
            getCompletedSubmissionForPlayer(completedRound, rightPlayerId),
          ),
          revealExactTiles,
        ),
        center: getCompletedCenter(completedRound, leftPlayerId),
        current: false,
        pendingSide: null,
      };
    }

    if (state.currentRound?.roundNumber === roundNumber) {
      const ownSubmission = getCurrentSubmissionForPlayer(
        state.currentRound,
        leftPlayerId,
      );
      const opponentSubmission = getCurrentSubmissionForPlayer(
        state.currentRound,
        rightPlayerId,
      );
      const pendingSide =
        activePlayerId === leftPlayerId
          ? "mine"
          : activePlayerId === rightPlayerId
            ? "opponent"
            : null;
      return {
        roundNumber,
        mine: maskTileView(
          canPreview && previewPlayerId === leftPlayerId
            ? {
                color: getTileColor(selectedTile),
                tile: selectedTile,
                provisional: true,
              }
            : toTileView(ownSubmission),
          revealExactTiles,
        ),
        opponent: maskTileView(
          canPreview && previewPlayerId === rightPlayerId
            ? {
                color: getTileColor(selectedTile),
                tile: selectedTile,
                provisional: true,
              }
            : toTileView(opponentSubmission),
          revealExactTiles,
        ),
        center: "pending",
        current: true,
        pendingSide,
      };
    }

    return {
      roundNumber,
      mine: null,
      opponent: null,
      center: "future",
      current: false,
      pendingSide: null,
    };
  });
}

function maskTileView(
  tile: TileView | null,
  revealExactTiles: boolean,
): TileView | null {
  if (!tile || revealExactTiles) return tile;
  return { ...tile, tile: null };
}

function resolveFirstPlayerId(
  playerIds: readonly string[],
  hostPlayerId: string,
  config: Record<string, unknown>,
): string {
  const configuredFirstPlayerId = config.firstPlayerId;
  if (
    typeof configuredFirstPlayerId === "string" &&
    playerIds.includes(configuredFirstPlayerId)
  ) {
    return configuredFirstPlayerId;
  }

  const firstPlayerPolicy =
    config.firstPlayerPolicy === "host" ||
    config.firstPlayerPolicy === "guest" ||
    config.firstPlayerPolicy === "random"
      ? (config.firstPlayerPolicy as FirstPlayerPolicy)
      : "random";

  if (firstPlayerPolicy === "host") return hostPlayerId;
  if (firstPlayerPolicy === "guest") {
    return (
      playerIds.find((playerId) => playerId !== hostPlayerId) ?? hostPlayerId
    );
  }

  const randomValues = new Uint32Array(1);
  crypto.getRandomValues(randomValues);
  return playerIds[randomValues[0] % playerIds.length] ?? hostPlayerId;
}

function tileColorClass(color: TileColor): string {
  return color === "WHITE" ? "white" : "black";
}

function RoundTile({
  tile,
  side,
  pending,
}: {
  readonly tile: TileView | null;
  readonly side: "mine" | "opponent";
  readonly pending: boolean;
}) {
  const classes = [
    "baw-round-tile",
    side,
    tile ? tileColorClass(tile.color) : "empty",
    tile && tile.tile === null ? "concealed" : "",
    tile?.provisional ? "provisional" : "",
    pending ? "pending-slot" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={classes}>
      {pending && !tile ? (
        <span className="baw-tile-spinner" aria-hidden="true" />
      ) : tile?.tile ? (
        <span>{tile.tile}</span>
      ) : (
        <span className="baw-tile-dot" />
      )}
    </div>
  );
}

function RoundCenterBadge({
  center,
  roundNumber,
}: {
  readonly center: RoundCenter;
  readonly roundNumber: number;
}) {
  if (center === "future") {
    return <span className="baw-round-number">{roundNumber}</span>;
  }
  if (center === "pending") {
    return (
      <span className="baw-pending-dots" aria-hidden="true">
        <i />
        <i />
        <i />
      </span>
    );
  }

  const label = center === "win" ? "승" : center === "loss" ? "패" : "무";
  return <span className={`baw-result-badge ${center}`}>{label}</span>;
}

function RoundBoard({
  state,
  leftPlayerId,
  rightPlayerId,
  viewerPlayerId,
  selectedTile,
  getPlayerName,
  revealExactTiles,
}: {
  readonly state: BlackAndWhiteViewerState;
  readonly leftPlayerId: string | null;
  readonly rightPlayerId: string | null;
  readonly viewerPlayerId: string | null;
  readonly selectedTile: TileNumber | null;
  readonly getPlayerName: (playerId: string) => string;
  readonly revealExactTiles: boolean;
}) {
  const me = getPlayer(state, leftPlayerId);
  const opponent = getPlayer(state, rightPlayerId);
  const slots = buildRoundSlots(
    state,
    leftPlayerId,
    rightPlayerId,
    viewerPlayerId,
    selectedTile,
    revealExactTiles,
  );

  return (
    <section className="baw-board" data-testid="black-and-white-round-board">
      <div className="baw-board-header">
        <div className="baw-player-head mine">
          <span>{getPlayerName(leftPlayerId ?? "")}</span>
          <strong>{me?.score ?? 0}</strong>
        </div>
        <div className="baw-score-track">
          {state.completedRounds.length}/{state.maxRounds}
        </div>
        <div className="baw-player-head opponent">
          <span>{getPlayerName(rightPlayerId ?? "")}</span>
          <strong>{opponent?.score ?? 0}</strong>
        </div>
      </div>

      <div className="baw-round-list">
        {slots.map((slot) => (
          <div
            key={slot.roundNumber}
            className={`baw-round-row ${slot.center} ${
              slot.current ? "current" : ""
            }`}
            data-testid={`black-and-white-round-${slot.roundNumber}`}
          >
            <RoundTile
              tile={slot.mine}
              side="mine"
              pending={slot.pendingSide === "mine"}
            />
            <div className="baw-round-center">
              <RoundCenterBadge
                center={slot.center}
                roundNumber={slot.roundNumber}
              />
            </div>
            <RoundTile
              tile={slot.opponent}
              side="opponent"
              pending={slot.pendingSide === "opponent"}
            />
          </div>
        ))}
      </div>
    </section>
  );
}

function TileHand({
  remainingTiles,
  selectedTile,
  canSelect,
  onSelect,
}: {
  readonly remainingTiles: readonly TileNumber[];
  readonly selectedTile: TileNumber | null;
  readonly canSelect: boolean;
  readonly onSelect: (tile: TileNumber) => void;
}) {
  return (
    <div className="baw-hand" data-testid="black-and-white-hand">
      {BLACK_AND_WHITE_TILE_NUMBERS.map((tile) => {
        const available = remainingTiles.includes(tile);
        const selected = selectedTile === tile;
        const disabled = !available || !canSelect;
        return (
          <button
            key={tile}
            type="button"
            className={`baw-hand-tile ${tileColorClass(getTileColor(tile))} ${
              selected ? "selected" : ""
            } ${available ? "" : "used"}`}
            data-testid={`black-and-white-tile-${tile}`}
            disabled={disabled}
            aria-pressed={selected}
            onClick={() => onSelect(tile)}
          >
            {tile}
          </button>
        );
      })}
    </div>
  );
}

function ObserverHands({
  players,
  getPlayerName,
}: {
  readonly players: readonly ViewerPlayer[];
  readonly getPlayerName: (playerId: string) => string;
}) {
  return (
    <div className="baw-observer-hands" data-testid="black-and-white-god-view">
      {players.map((player) => (
        <div
          key={player.playerId}
          className="baw-observer-hand-row"
          data-testid={`black-and-white-god-view-hand-${player.playerId}`}
        >
          <strong>{getPlayerName(player.playerId)}</strong>
          <div className="baw-observer-hand-tiles">
            {BLACK_AND_WHITE_TILE_NUMBERS.map((tile) => {
              const available = player.remainingTiles?.includes(tile) ?? false;
              return (
                <span
                  key={tile}
                  className={`baw-hand-tile ${tileColorClass(getTileColor(tile))} ${
                    available ? "" : "used"
                  }`}
                >
                  {tile}
                </span>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function getTurnText(
  state: BlackAndWhiteViewerState,
  myPlayerId: string | null,
): string {
  if (myPlayerId === null) {
    if (state.status === "COMPLETED") return "관전 종료";
    const round = state.currentRound;
    if (!round) return "관전 중";
    const role = round.phase === "FIRST_SELECTION" ? "선공" : "후공";
    return `${role} 선택 중`;
  }
  if (state.status === "COMPLETED") {
    if (state.winnerId === null) return "무승부";
    return state.winnerId === myPlayerId ? "승리" : "패배";
  }
  const round = state.currentRound;
  if (!round) return "대기 중";
  const activePlayerId = getActivePlayerId(round);
  const role = round.phase === "FIRST_SELECTION" ? "선공" : "후공";
  if (activePlayerId === myPlayerId) {
    return `내 차례 · ${role} 선택`;
  }
  return `상대 차례 · ${role} 대기`;
}

interface ResultInfo {
  readonly title: string;
  readonly summary: string;
  readonly detail: string;
  readonly tone: "win" | "loss" | "draw";
}

function buildResultInfo(
  state: BlackAndWhiteViewerState,
  viewerPlayerId: string | null,
  getPlayerName: (playerId: string) => string,
): ResultInfo | null {
  if (state.status !== "COMPLETED") return null;

  const [first, second] = state.players;
  const scoreLine =
    first && second
      ? `${getPlayerName(first.playerId)} ${first.score} : ${second.score} ${getPlayerName(second.playerId)}`
      : "";

  if (state.winnerId === null) {
    return {
      title: "무승부",
      summary: "무",
      detail: scoreLine,
      tone: "draw",
    };
  }

  if (viewerPlayerId === null) {
    return {
      title: `${getPlayerName(state.winnerId)} 승리`,
      summary: "종료",
      detail: scoreLine,
      tone: "win",
    };
  }

  const won = state.winnerId === viewerPlayerId;
  return {
    title: won ? "승리" : "패배",
    summary: won ? "승리" : "패배",
    detail: scoreLine,
    tone: won ? "win" : "loss",
  };
}

function ResultModal({
  result,
  onClose,
  onLeave,
}: {
  readonly result: ResultInfo;
  readonly onClose: () => void;
  readonly onLeave: () => void;
}) {
  return (
    <div
      className="baw-modal-backdrop"
      data-testid="black-and-white-result-modal"
    >
      <section
        className="baw-result-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="black-and-white-result-title"
      >
        <button
          type="button"
          className="baw-modal-close"
          data-testid="black-and-white-result-close"
          aria-label="결과 닫기"
          onClick={onClose}
        >
          닫기
        </button>
        <span className={`baw-result-mark ${result.tone}`}>
          {result.summary}
        </span>
        <h2
          id="black-and-white-result-title"
          data-testid="black-and-white-result-title"
        >
          {result.title}
        </h2>
        <p data-testid="black-and-white-result-summary">{result.detail}</p>
        <div className="baw-result-actions">
          <button
            type="button"
            className="baw-button primary"
            data-testid="black-and-white-result-lobby"
            onClick={onLeave}
          >
            로비로 돌아가기
          </button>
          <button type="button" className="baw-button" onClick={onClose}>
            계속 보기
          </button>
        </div>
      </section>
    </div>
  );
}

interface BlackAndWhiteObserverViewProps {
  readonly viewerState: BlackAndWhiteViewerState;
  readonly connectionLabel: string;
  readonly getPlayerName: (playerId: string) => string;
  readonly leaveLabel: string;
  readonly onLeave: () => void;
}

export function BlackAndWhiteObserverView({
  viewerState,
  connectionLabel,
  getPlayerName,
  leaveLabel,
  onLeave,
}: BlackAndWhiteObserverViewProps) {
  const [resultModalOpen, setResultModalOpen] = useState(false);
  const leftPlayerId = viewerState.players[0]?.playerId ?? null;
  const rightPlayerId = viewerState.players[1]?.playerId ?? null;
  const canUseObserverGodView = viewerState.observerGodViewEnabled === true;
  const revealExactTiles =
    viewerState.status === "COMPLETED" || canUseObserverGodView;
  const resultInfo = buildResultInfo(viewerState, null, getPlayerName);

  return (
    <div
      className="baw-screen"
      data-testid="game-screen"
      data-game="black-and-white"
    >
      <div className="baw-shell">
        <header className="baw-topbar">
          <div>
            <strong>흑과백</strong>
            <span>관전자 · {connectionLabel}</span>
          </div>
          <button type="button" className="baw-ghost-button" onClick={onLeave}>
            {leaveLabel}
          </button>
        </header>

        <RoundBoard
          state={viewerState}
          leftPlayerId={leftPlayerId}
          rightPlayerId={rightPlayerId}
          viewerPlayerId={null}
          selectedTile={null}
          getPlayerName={getPlayerName}
          revealExactTiles={revealExactTiles}
        />

        <footer className="baw-action-panel">
          <div className="baw-action-status">
            <strong>{getTurnText(viewerState, null)}</strong>
            <span>
              {viewerState.currentRound
                ? `${viewerState.currentRound.roundNumber}R`
                : "종료"}
            </span>
          </div>
          {resultInfo ? (
            <button
              type="button"
              className="baw-button primary"
              data-testid="black-and-white-result-open"
              onClick={() => setResultModalOpen(true)}
            >
              결과 보기
            </button>
          ) : (
            <div
              className="baw-observer-pill"
              data-testid="black-and-white-observer-pill"
            >
              {canUseObserverGodView ? "관전 · 전체 손패" : "관전"}
            </div>
          )}
          {canUseObserverGodView && (
            <ObserverHands
              players={viewerState.players}
              getPlayerName={getPlayerName}
            />
          )}
        </footer>
      </div>
      {resultInfo && resultModalOpen && (
        <ResultModal
          result={resultInfo}
          onClose={() => setResultModalOpen(false)}
          onLeave={onLeave}
        />
      )}
    </div>
  );
}

export function GameScreen() {
  const { user } = useAuth();
  const { currentRoom } = useLobby();
  const lobbyActions = useLobbyActions();
  const { viewerState, lastError } = useBlackAndWhiteMultiplayerStore();
  const actions = blackAndWhiteMultiplayerActions;
  const [selectedTile, setSelectedTile] = useState<TileNumber | null>(null);
  const [resultModalOpen, setResultModalOpen] = useState(false);

  const roomId = currentRoom?.id ?? null;
  const isHost = currentRoom?.hostUserId === user?.id;
  const actionsRef = useRef(actions);
  const lobbyActionsRef = useRef(lobbyActions);

  useEffect(() => {
    actionsRef.current = actions;
    lobbyActionsRef.current = lobbyActions;
  }, [lobbyActions]);

  const { connectionStatus } = useGameEventsWS(roomId, {
    onGameEvent: () => {
      if (roomId) void actionsRef.current.replayEvents(roomId);
    },
    onViewerState: (state, seq) => {
      actionsRef.current.applyRemoteEvent(state, seq);
    },
    onRoomUpdated: (room) => {
      lobbyActionsRef.current.updateCurrentRoom(room);
    },
    onReconnect: () => {
      if (roomId) void actionsRef.current.replayEvents(roomId);
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: room id + host bit are the stable init boundaries; room_updated refreshes participant metadata.
  useEffect(() => {
    if (!roomId || !currentRoom || !user) return;
    const participantIds = currentRoom.participantUserIds ?? [];
    if (participantIds.length !== 2) return;

    if (isHost) {
      const firstPlayerId = resolveFirstPlayerId(
        participantIds,
        currentRoom.hostUserId,
        currentRoom.config,
      );
      const observerGodViewEnabled =
        currentRoom.config.observerGodViewEnabled === true;
      void actions.initializeAsHost(
        roomId,
        participantIds,
        currentRoom.hostUserId,
        firstPlayerId,
        observerGodViewEnabled,
      );
    } else {
      void actions.initializeAsGuest(roomId);
    }

    return () => {
      actions.reset();
    };
  }, [roomId, currentRoom?.id, user?.id, isHost]);

  const selectionResetKey = `${viewerState?.currentRound?.roundNumber ?? "done"}:${viewerState?.currentRound?.phase ?? "none"}`;
  // biome-ignore lint/correctness/useExhaustiveDependencies: the key intentionally resets local tile preview whenever the server-projected turn boundary changes.
  useEffect(() => {
    setSelectedTile(null);
  }, [selectionResetKey]);

  const viewerPlayerId = viewerState?.viewerPlayerId ?? null;
  const leftPlayerId =
    viewerPlayerId ?? viewerState?.players[0]?.playerId ?? null;
  const rightPlayerId = viewerState
    ? ((viewerPlayerId === null
        ? viewerState.players[1]?.playerId
        : getOpponentId(viewerState, viewerPlayerId)) ?? null)
    : null;
  const me = viewerState ? getPlayer(viewerState, viewerPlayerId) : null;
  const remainingTiles = me?.remainingTiles ?? [];
  const currentOwnSubmission = viewerState?.currentRound
    ? getCurrentSubmissionForPlayer(viewerState.currentRound, viewerPlayerId)
    : null;
  const displayRemainingTiles =
    currentOwnSubmission?.tile === null ||
    currentOwnSubmission?.tile === undefined
      ? remainingTiles
      : remainingTiles.filter((tile) => tile !== currentOwnSubmission.tile);
  const activePlayerId = getActivePlayerId(viewerState?.currentRound ?? null);
  const canSelect =
    viewerState?.status === "IN_PROGRESS" && activePlayerId === viewerPlayerId;
  const canSubmit =
    canSelect && selectedTile !== null && remainingTiles.includes(selectedTile);
  const isObserver = viewerPlayerId === null;
  const canUseObserverGodView =
    isObserver && viewerState?.observerGodViewEnabled === true;
  const revealExactTiles =
    viewerState?.status === "COMPLETED" || !isObserver || canUseObserverGodView;
  const actionPanelTurnClass = isObserver
    ? "observer"
    : canSelect
      ? "mine-turn"
      : viewerState?.currentRound
        ? "opponent-turn"
        : "";

  const getPlayerName = (playerId: string): string =>
    playerId === viewerPlayerId
      ? "나"
      : getParticipantDisplayName(currentRoom, playerId, "상대");
  const resultInfo = viewerState
    ? buildResultInfo(viewerState, viewerPlayerId, getPlayerName)
    : null;
  const resultKey = resultInfo
    ? `${viewerState?.gameId}:${viewerState?.winnerId ?? "draw"}:${viewerState?.completedRounds.length}`
    : null;
  const autoOpenedResultKey = useRef<string | null>(null);

  useEffect(() => {
    if (resultKey && autoOpenedResultKey.current !== resultKey) {
      autoOpenedResultKey.current = resultKey;
      setResultModalOpen(true);
    }
  }, [resultKey]);

  function leaveGame(): void {
    actions.reset();
    void lobbyActions.leaveGame();
    navigateTo("/black-and-white/lobby");
  }

  async function submitSelectedTile(): Promise<void> {
    if (!canSubmit || selectedTile === null) return;
    const result = await actions.submitTile(selectedTile);
    if (result.success) {
      setSelectedTile(null);
    }
  }

  if (!currentRoom) {
    return (
      <div className="baw-screen" data-testid="black-and-white-screen">
        <section className="baw-empty-card">
          <h1>흑과백</h1>
          <p>게임 방이 없습니다.</p>
          <button type="button" className="baw-button" onClick={leaveGame}>
            로비로 이동
          </button>
        </section>
      </div>
    );
  }

  if (!viewerState) {
    return (
      <div
        className="baw-screen"
        data-testid="black-and-white-screen"
        data-game="black-and-white"
      >
        <section className="baw-empty-card" aria-live="polite">
          <span className="baw-spinner" />
          <h1>흑과백</h1>
          <p>
            {connectionStatus === "connected"
              ? "게임 상태를 준비 중입니다."
              : "게임 서버에 연결 중입니다."}
          </p>
          {lastError && <div className="baw-toast">{lastError.error}</div>}
        </section>
      </div>
    );
  }

  return (
    <div
      className="baw-screen"
      data-testid="game-screen"
      data-game="black-and-white"
    >
      <div className="baw-shell">
        <header className="baw-topbar">
          <div>
            <strong>흑과백</strong>
            <span>{connectionStatus}</span>
          </div>
          <button
            type="button"
            className="baw-ghost-button"
            onClick={leaveGame}
          >
            나가기
          </button>
        </header>

        {lastError && <div className="baw-toast">{lastError.error}</div>}

        <RoundBoard
          state={viewerState}
          leftPlayerId={leftPlayerId}
          rightPlayerId={rightPlayerId}
          viewerPlayerId={viewerPlayerId}
          selectedTile={selectedTile}
          getPlayerName={getPlayerName}
          revealExactTiles={revealExactTiles}
        />

        <footer
          className={`baw-action-panel ${actionPanelTurnClass}`}
          data-testid="black-and-white-action-panel"
        >
          <div className="baw-action-status">
            <strong>{getTurnText(viewerState, viewerPlayerId)}</strong>
            <span>
              {viewerState.currentRound
                ? `${viewerState.currentRound.roundNumber}R`
                : "종료"}
            </span>
          </div>
          {resultInfo ? (
            <>
              <button
                type="button"
                className="baw-button primary"
                data-testid="black-and-white-result-open"
                onClick={() => setResultModalOpen(true)}
              >
                결과 보기
              </button>
              {isObserver && canUseObserverGodView && (
                <ObserverHands
                  players={viewerState.players}
                  getPlayerName={getPlayerName}
                />
              )}
            </>
          ) : isObserver ? (
            <>
              <div
                className="baw-observer-pill"
                data-testid="black-and-white-observer-pill"
              >
                {canUseObserverGodView ? "관전 · 전체 손패" : "관전"}
              </div>
              {canUseObserverGodView && (
                <ObserverHands
                  players={viewerState.players}
                  getPlayerName={getPlayerName}
                />
              )}
            </>
          ) : (
            <>
              <TileHand
                remainingTiles={displayRemainingTiles}
                selectedTile={selectedTile}
                canSelect={canSelect}
                onSelect={setSelectedTile}
              />
              <button
                type="button"
                className="baw-button primary"
                data-testid="black-and-white-submit"
                disabled={!canSubmit}
                onClick={() => void submitSelectedTile()}
              >
                제출
              </button>
            </>
          )}
        </footer>
      </div>
      {resultInfo && resultModalOpen && (
        <ResultModal
          result={resultInfo}
          onClose={() => setResultModalOpen(false)}
          onLeave={leaveGame}
        />
      )}
    </div>
  );
}
